import { sum } from 'lodash';
import uuid from 'react-uuid';
import { GlCodesObject } from 'src/services/useGlCodesApi';
import { InvoiceGlBreakoutItem } from 'src/services/useInvoiceGlBreakoutApi';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { round } from 'src/utils/number';

interface Totals {
  subTotal: number;
  taxableAmount: number;
}

interface GrandTotals {
  grandSubTotal: number;
  grandTaxableSubTotal: number;
}

export const generateGlCodesFromLineItems = (
  lineItems: InvoiceLineItem[],
  discount: number,
  shipping: number,
  taxes: number,
  glCodesObject: GlCodesObject,
  previousGlCodes: InvoiceGlBreakoutItem[],
  isAutoBreakout: boolean,
  initialInvoiceGlBreakouts: InvoiceGlBreakoutItem[],
  hasGLChanged: boolean
) => {
  const newlyCreatedPreviousGlCodes: InvoiceGlBreakoutItem[] = previousGlCodes.filter(
    (item) => item.isNewlyAdded
  );

  const {
    notesMapping,
    discountMapping,
    shippingMapping,
    taxesMapping,
    expandMapping,
    itemsMapping,
  } = [...previousGlCodes].reduce<{
    notesMapping: Record<string, string>;
    discountMapping: Record<string, number>;
    shippingMapping: Record<string, number>;
    taxesMapping: Record<string, number>;
    expandMapping: Record<string, boolean>;
    itemsMapping: Record<string, any[] | undefined>;
  }>(
    (acc, glCode) => {
      acc.notesMapping[glCode.glCode] = glCode.notes;
      acc.discountMapping[glCode.glCode] = glCode.discountAmt ?? 0;
      acc.shippingMapping[glCode.glCode] = glCode.shipCostAmt ?? 0;
      acc.taxesMapping[glCode.glCode] = glCode.salesTaxAmt ?? 0;
      acc.expandMapping[glCode.glCode] = !!glCode?.expanded;
      acc.itemsMapping[glCode.glCode] = glCode.items;
      return acc;
    },
    {
      notesMapping: {},
      discountMapping: {},
      shippingMapping: {},
      taxesMapping: {},
      expandMapping: {},
      itemsMapping: {},
    }
  );

  const totals = lineItems?.reduce<Record<string, Totals>>((acc, lineItem) => {
    const subTotal = Number(lineItem.subTotal);
    if (acc[lineItem.glCd]) {
      acc[lineItem.glCd] = {
        taxableAmount: sum([acc[lineItem.glCd].taxableAmount, lineItem?.isTaxable ? subTotal : 0]),
        subTotal: sum([acc[lineItem.glCd].subTotal, subTotal]),
      };
    } else {
      acc[lineItem.glCd] = {
        taxableAmount: lineItem?.isTaxable ? subTotal : 0,
        subTotal,
      };
    }

    return acc;
  }, {});

  const linItemTotals = Object.values(totals).reduce(
    (acc, value) => acc + Number(value.subTotal),
    0
  );

  const { grandSubTotal, grandTaxableSubTotal } = Object.values(totals).reduce<GrandTotals>(
    (acc, value) => {
      acc.grandSubTotal = round(Number(acc.grandSubTotal) + Number(value.subTotal), 2);
      acc.grandTaxableSubTotal = round(
        Number(acc.grandTaxableSubTotal) + Number(value.taxableAmount),
        2
      );
      return acc;
    },
    {
      grandSubTotal: 0,
      grandTaxableSubTotal: 0,
    }
  );

  let { glCodes, grandTotal } = Object.entries(totals).reduce<{
    glCodes: InvoiceGlBreakoutItem[];
    grandTotal: number;
  }>(
    (acc, [id, totals]) => {
      const taxPercentage = grandTaxableSubTotal ? totals.taxableAmount / grandTaxableSubTotal : 0;
      const otherPercentage = grandSubTotal ? totals.subTotal / grandSubTotal : 0;
      const initialGLCodeDiscount =
        initialInvoiceGlBreakouts.find((glCode) => glCode.glCode === id)?.discountAmt ?? 0;
      const initialGLCodeShipping =
        initialInvoiceGlBreakouts.find((glCode) => glCode.glCode === id)?.shipCostAmt ?? 0;
      const initialGLCodeTaxes =
        initialInvoiceGlBreakouts.find((glCode) => glCode.glCode === id)?.salesTaxAmt ?? 0;

      let discountAmt = round(
        isAutoBreakout
          ? hasGLChanged
            ? otherPercentage * discount
            : initialGLCodeDiscount * -1
          : discountMapping[id],
        2
      );
      let shippingAmt = round(
        isAutoBreakout
          ? hasGLChanged
            ? otherPercentage * shipping
            : initialGLCodeShipping
          : shippingMapping[id],
        2
      );
      let taxesAmt = round(
        isAutoBreakout
          ? hasGLChanged
            ? taxPercentage * taxes
            : initialGLCodeTaxes
          : taxesMapping[id],
        2
      );
      const glTotal = round(totals.subTotal + shippingAmt + taxesAmt - discountAmt, 2);
      acc.grandTotal = round(acc.grandTotal + glTotal, 2);

      acc.glCodes.push({
        glCodeId: glCodesObject?.[id]?.id,
        glCode: id,
        glLabel: glCodesObject?.[id]?.label,
        glBalance: glCodesObject?.[id]?.balance,
        glSubTotal: round(totals.subTotal, 2),
        taxableAmount: round(totals.taxableAmount, 2),
        discountAmt,
        shipCostAmt: shippingAmt,
        salesTaxAmt: taxesAmt,
        glTotal: glTotal,
        notes: notesMapping[id],
        rowId: uuid(),
        taxPercentage,
        expanded: expandMapping[id],
        subTotalAmt: round(totals.subTotal, 2),
        taxableAmt: round(totals.taxableAmount, 2),
        items: itemsMapping[id],
      });
      return acc;
    },
    { glCodes: [], grandTotal: 0 }
  );

  if (!isAutoBreakout) {
    const existingGlCodesIds = glCodes.map((glCode) => glCode.glCodeId);
    const nonRepetitiveNewlyCreatedPreviousGlCodes = newlyCreatedPreviousGlCodes
      .filter((glCode) => !existingGlCodesIds.includes(glCode.glCodeId))
      .map((g) => ({
        ...g,
        glTotal: Number(g.shipCostAmt) + Number(g.salesTaxAmt) - Number(g.discountAmt),
      }));
    glCodes = glCodes.concat(nonRepetitiveNewlyCreatedPreviousGlCodes);
  } else {
    glCodes = fixRoundup(glCodes, discount, shipping, taxes);
  }
  glCodes = fixGlSubtotalRoundup(glCodes, round(linItemTotals, 2), round(grandSubTotal, 2));
  return { glCodes, grandTotal };
};

const fixRoundup = (
  glCodes: InvoiceGlBreakoutItem[],
  discount: number,
  shipping: number,
  taxes: number
) => {
  const { discountAddup, shippingAddup, taxesAddup } = glCodes.reduce<{
    discountAddup: number;
    shippingAddup: number;
    taxesAddup: number;
  }>(
    (acc, glCode) => {
      acc.discountAddup += glCode.discountAmt;
      acc.shippingAddup += glCode.shipCostAmt;
      acc.taxesAddup += glCode.salesTaxAmt;
      return acc;
    },
    { discountAddup: 0, shippingAddup: 0, taxesAddup: 0 }
  );
  const discountDifference = round(discount - discountAddup, 2);
  const shippingDifference = round(shipping - shippingAddup, 2);
  const taxesDifference = round(taxes - taxesAddup, 2);

  const indexOfItemWithBigDiscount = getIndexWithHighestBalance(glCodes, 'discountAmt');
  if (glCodes[indexOfItemWithBigDiscount]) {
    glCodes[indexOfItemWithBigDiscount].discountAmt = round(
      glCodes[indexOfItemWithBigDiscount].discountAmt + discountDifference,
      2
    );
  }

  const indexOfItemWithBigShipCostAmt = getIndexWithHighestBalance(glCodes, 'shipCostAmt');
  if (glCodes[indexOfItemWithBigShipCostAmt]) {
    glCodes[indexOfItemWithBigShipCostAmt].shipCostAmt = round(
      glCodes[indexOfItemWithBigShipCostAmt].shipCostAmt + shippingDifference,
      2
    );
  }

  const indexOfItemWithSalesTaxAmt = getIndexWithHighestBalance(glCodes, 'salesTaxAmt');
  if (glCodes[indexOfItemWithSalesTaxAmt] && glCodes[indexOfItemWithSalesTaxAmt].taxPercentage) {
    glCodes[indexOfItemWithSalesTaxAmt].salesTaxAmt = round(
      glCodes[indexOfItemWithSalesTaxAmt].salesTaxAmt + taxesDifference,
      2
    );
  }

  glCodes.forEach(
    (g) => (g.glTotal = round(g.glSubTotal + g.shipCostAmt + g.salesTaxAmt - g.discountAmt, 2))
  );
  return glCodes;
};

const getIndexWithHighestBalance = (
  glCodes: InvoiceGlBreakoutItem[],
  type: 'discountAmt' | 'shipCostAmt' | 'salesTaxAmt'
) => {
  if (!Array.isArray(glCodes) || glCodes.length === 0) {
    return -1;
  }

  let maxBalance = glCodes[0][type];
  let maxBalanceIndex = 0;

  for (let i = 1; i < glCodes.length; i++) {
    if (glCodes[i][type] > maxBalance) {
      maxBalance = glCodes[i][type];
      maxBalanceIndex = i;
    }
  }

  return maxBalanceIndex;
};

const fixGlSubtotalRoundup = (
  glCodes: InvoiceGlBreakoutItem[],
  linItemTotals: number,
  grandSubTotal: number
) => {
  if (linItemTotals !== grandSubTotal) {
    let difference = round(grandSubTotal - linItemTotals, 2);
    const penny = Math.sign(difference) * 0.01;
    const sortedBreakouts = [...glCodes].sort((a, b) => {
      if (linItemTotals < 0) {
        // Ascending by subTotalAmt
        if (a.subTotalAmt !== b.subTotalAmt) {
          return a.subTotalAmt - b.subTotalAmt;
        }
      } else {
        // Descending by subTotalAmt
        if (b.subTotalAmt !== a.subTotalAmt) {
          return b.subTotalAmt - a.subTotalAmt;
        }
      }
      // ascending by glCodeId
      if (a.glCodeId !== b.glCodeId) {
        return a.glCodeId - b.glCodeId;
      }
      return 0;
    });

    for (let i = 0; i < sortedBreakouts.length && difference !== 0; i++) {
      sortedBreakouts[i].glSubTotal = round(sortedBreakouts[i].glSubTotal - penny, 2);
      sortedBreakouts[i].subTotalAmt = round(sortedBreakouts[i].subTotalAmt - penny, 2);
      if (sortedBreakouts[i].taxableAmt) {
        sortedBreakouts[i].taxableAmt = round(sortedBreakouts[i].taxableAmt - penny, 2);
        sortedBreakouts[i].taxableAmount = round(
          (sortedBreakouts[i].taxableAmount || 0) - penny,
          2
        );
      }
      difference = round(difference - penny, 2);
    }
    glCodes = sortedBreakouts;
  }
  glCodes.forEach(
    (g) => (g.glTotal = round(g.glSubTotal + g.shipCostAmt + g.salesTaxAmt - g.discountAmt, 2))
  );
  return glCodes;
};
